import { ReactNode, useEffect, useId, useRef, useState } from "react";
import "./BookingInfoPopover.scss";

interface BookingInfoPopoverProps {
  title: string;
  /** Trigger text. The default suits the booking cards it was built for. */
  label?: string;
  children: ReactNode;
}

function InfoIcon() {
  return (
    <svg
      aria-hidden="true"
      viewBox="0 0 20 20"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <circle cx="10" cy="10" r="8.25" stroke="currentColor" strokeWidth="1.7" />
      <path
        d="M10 8.75V14"
        stroke="currentColor"
        strokeWidth="1.7"
        strokeLinecap="round"
      />
      <circle cx="10" cy="6" r="1" fill="currentColor" />
    </svg>
  );
}

function BookingInfoPopover({
  title,
  label = "Tryck för mer information om din bokning",
  children,
}: BookingInfoPopoverProps) {
  const contentId = useId();
  const headingId = `${contentId}-heading`;

  // Hover and focus open it on desktop through CSS alone, but a tap on iOS
  // does not focus a button, so "Tryck för…" needs an explicit toggle too.
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const close = (event: PointerEvent) => {
      if (!rootRef.current?.contains(event.target as Node)) setOpen(false);
    };
    document.addEventListener("pointerdown", close);
    return () => document.removeEventListener("pointerdown", close);
  }, [open]);

  return (
    <div
      ref={rootRef}
      className={`booking-info-popover ${open ? "is-open" : ""}`}
    >
      <button
        type="button"
        className="booking-info-popover__trigger"
        aria-controls={contentId}
        aria-haspopup="dialog"
        aria-expanded={open}
        onClick={() => setOpen((prev) => !prev)}
      >
        <InfoIcon />
        <span>{label}</span>
      </button>

      <div
        id={contentId}
        className="booking-info-popover__panel"
        role="dialog"
        aria-labelledby={headingId}
      >
        <button
          type="button"
          className="booking-info-popover__close"
          aria-label="Stäng"
          onClick={() => setOpen(false)}
        >
          <svg aria-hidden="true" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg">
            <path
              d="M5 5L15 15M15 5L5 15"
              stroke="currentColor"
              strokeWidth="1.7"
              strokeLinecap="round"
            />
          </svg>
        </button>
        <div id={headingId} className="booking-info-popover__heading">
          <span>{title}</span>
        </div>
        <div className="booking-info-popover__content">{children}</div>
      </div>
    </div>
  );
}

export default BookingInfoPopover;
